import React, { useState, useEffect, useCallback } from 'react';
import { GridComponent, ColumnsDirective, ColumnDirective, Page, Toolbar, Sort, Filter, Inject, Edit, CommandColumn, ForeignKey } from '@syncfusion/ej2-react-grids';
import { DialogComponent } from '@syncfusion/ej2-react-popups';
import { ButtonComponent } from '@syncfusion/ej2-react-buttons';
import { type ResourceDto } from '../api/models/ResourceDto';
import { type ResourceRequirementResourceDto } from '../api/models/ResourceRequirementResourceDto';
import { ResourceControllerService } from '../api/services/ResourceControllerService';
import { useTranslation } from 'react-i18next';
import { useDropdownFormInitializer } from '../hooks/useDropdownFormInitializer';

interface ResourceSelectionDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (resources: ResourceRequirementResourceDto[]) => void;
  initialResources: ResourceRequirementResourceDto[];
  resourceRequirementName?: string;
}

const ResourceSelectionDialog: React.FC<ResourceSelectionDialogProps> = ({
  isOpen,
  onClose,
  onSave,
  initialResources,
  resourceRequirementName
}) => {
  const { t } = useTranslation();
  const [resources, setResources] = useState<ResourceDto[]>([]);
  const [selectedResources, setSelectedResources] = useState<(ResourceRequirementResourceDto & { rowId: string })[]>([]);
  const [loading, setLoading] = useState(true);
  const gridRef = React.useRef<any>(null);

  // Load data function for the hook
  const loadData = async () => {
    const resourcesData = await ResourceControllerService.findAll();
    
    // Ensure all resources have proper uuid and name fields
    const validResources = resourcesData.filter(resource => resource.uuid && resource.name);
    
    return { resources: validResources };
  };

  // Set state functions for the hook
  const setStateFunctions = {
    resources: setResources
  };

  // Use the custom hook for proper timing
  useDropdownFormInitializer(
    isOpen,
    loadData,
    setStateFunctions,
    () => {
      setLoading(false);
    },
    {}
  );

  // Initialize selected resources when dialog opens
  useEffect(() => {
    if (isOpen) {
      // Add rowId to each resource if it doesn't have one
      const resourcesWithRowIds = initialResources.map(resource => ({
        ...resource,
        rowId: (resource as any).rowId || `row_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
      }));
      setSelectedResources(resourcesWithRowIds);
    }
  }, [isOpen, initialResources]);

  const dialogContent = useCallback(() => {
    // Find resource by ID for display
    const findResourceById = (resourceId: number | undefined) => {
      if (!resourceId) {
        return null;
      }

      return resources.find(resource => resource.uuid === resourceId.toString());
    };

    const handleSave = () => {
      // Remove rowId from resources before saving
      const resourcesToSave = selectedResources.map(({ rowId, ...resource }) => resource);
      onSave(resourcesToSave);
    };

    return (
      <div className="p-4">
        <div className="space-y-4">
          {/* Resources Grid */}
          <div>
            <div className="mb-2">
              <h5 className="text-md font-semibold">
                {t('routing.form.resources')} ({selectedResources.length})
              </h5>
            </div>
            
            <div className="h-64">
              {loading ? (
                <div className="h-full flex items-center justify-center">
                  <div className="text-center text-gray-500">
                    <div className="text-md font-medium">{t('common.loading', 'Loading...')}</div>
                  </div>
                </div>
              ) : (
                <GridComponent
                  ref={gridRef}
                  key={`grid-${isOpen ? 'open' : 'closed'}-${resources.length}-${selectedResources.length}`}
                  dataSource={selectedResources}
                  height="100%"
                  allowSorting={true}
                  allowFiltering={false}
                  locale="de-DE"
                  toolbar={[
                    { text: t('actions.add'), tooltipText: t('actions.add'), prefixIcon: 'e-add', id: 'add' },
                    { text: t('actions.delete'), tooltipText: t('actions.delete'), prefixIcon: 'e-delete', id: 'delete' }
                  ]}
                  toolbarClick={(args) => {
                    if (!args.item) return;
                    
                    switch (args.item.text) {
                      case t('actions.add'):
                        // Create new resource and add to state
                        const newResource: ResourceRequirementResourceDto & { rowId: string } = {
                          resourceId: undefined,
                          quantity: 1,
                          rowId: `row_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
                        };
                        setSelectedResources(prev => [...prev, newResource]);
                        
                        // Start editing the new row after a short delay to ensure it's added
                        setTimeout(() => {
                          if (gridRef.current) {
                            const lastIndex = selectedResources.length; // New row will be at this index
                            gridRef.current.selectRow(lastIndex);
                            gridRef.current.startEdit(lastIndex, 'resourceId');
                          }
                        }, 100);
                        break;
                      case t('actions.delete'):
                        // Handle deletion
                        if (gridRef.current) {
                          const selectedRowIndexes = gridRef.current.getSelectedRowIndexes();
                          if (selectedRowIndexes.length > 0) {
                            const updatedResources = selectedResources.filter((_, index) => !selectedRowIndexes.includes(index));
                            setSelectedResources(updatedResources);

                          }
                        }
                        break;
                    }
                  }}
                  editSettings={{ allowEditing: true, allowAdding: false, allowDeleting: true, mode: 'Normal' }}
                  actionComplete={(args) => {
                    if (args.requestType === 'save') {
                      if (args.action === 'edit' && args.data) {
                        // Update the selected resources when editing is complete
                        const updatedData = args.data;
                        if (args.rowIndex !== undefined) {
                          const updatedResources = [...selectedResources];
                          const rowIndex = args.rowIndex;
                          if (rowIndex >= 0 && rowIndex < updatedResources.length) {
                            updatedResources[rowIndex] = {
                              ...updatedResources[rowIndex],
                              ...updatedData
                            };
                            setSelectedResources(updatedResources);
                          }
                        }
                      }
                    } else if (args.requestType === 'delete') {
                      // Handle deletion - update state to match grid
                      setSelectedResources(prev => {
                        const remainingResources = prev.filter((_, index) => index !== args.rowIndex);
                        return remainingResources;
                      });
                    }
                  }}
                  actionBegin={(args) => {
                    if (args.requestType === 'save') {
                      // Prevent saving if resourceId is empty
                      if (args.data && !args.data.resourceId) {
                        args.cancel = true;
                        return;
                      }
                    }
                  }}
                >
                  <ColumnsDirective>
                    <ColumnDirective
                      field="rowId"
                      headerText=""
                      width={0}
                      isPrimaryKey={true}
                      visible={false}
                    />
                    <ColumnDirective
                      field="resourceId"
                      headerText={t('routing.form.resource')}
                      width={200}
                      editType="dropdownedit"
                      dataSource={resources}
                      foreignKeyValue="name"
                      foreignKeyField="id"
                      allowEditing={true}
                      validationRules={{ required: true }}
                      template={(props: any) => {
                        const resource = findResourceById(props.resourceId);
                        return resource?.name || (props.resourceId ? `ID: ${props.resourceId}` : '');
                      }}
                    />
                    <ColumnDirective
                      field="quantity"
                      headerText={t('routing.form.quantity')}
                      width={100}
                      textAlign="Right"
                      editType="numericedit"
                    />
                  </ColumnsDirective>
                  <Inject services={[Page, Toolbar, Sort, Filter, Edit, CommandColumn, ForeignKey]} />
                </GridComponent>
              )}
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-6">
          <ButtonComponent 
            onClick={onClose} 
            cssClass="e-outline"
            type="button"
          >
            {t('actions.cancel')}
          </ButtonComponent>
          <ButtonComponent 
            onClick={handleSave}
            isPrimary={true}
            type="button"
          >
            {t('actions.save')}
          </ButtonComponent>
        </div>
      </div>
    );
  }, [loading, selectedResources, resources, gridRef, isOpen, t, setSelectedResources, onClose, onSave]);

  if (!isOpen) return null;

  return (
    <DialogComponent
      width="800px"
      isModal={true}
      visible={isOpen}
      header={t('routing.form.resources') + (resourceRequirementName ? ` (${resourceRequirementName})` : '')}
      showCloseIcon={true}
      closeOnEscape={false}
      allowDragging={true}
      target="#root"
      animationSettings={{ effect: 'None' }}
      beforeClose={(e) => {
        e.cancel = true;
        onClose();
      }}
    >
      {dialogContent()}
    </DialogComponent>
  );
};

export default ResourceSelectionDialog; 